import { UserAdministrationDto } from '@act/shared/data-transfer-objects';
import {
  createEntityAdapter,
  createSlice,
  EntityAdapter,
  PayloadAction,
} from '@reduxjs/toolkit';
import { userAdministratorRequests } from 'redux/requests/user-administrator.requests';
import { useAppSelector } from 'redux/storeHooks';
import {
  UserAdminState,
  UserAdminFiltersType,
} from 'redux/types/userAdminTypes';
import { createApiThunk } from 'redux/utils/apiThunk';
import { PersistConfig, persistReducer } from 'redux-persist';
import localStorage from 'redux-persist/lib/storage';
import autoMergeLevel1 from 'redux-persist/es/stateReconciler/autoMergeLevel1';

// THUNKS/ACTIONS ==============================================================
export const getUsers = createApiThunk(userAdministratorRequests.getUsers);
export const createUser = createApiThunk(userAdministratorRequests.createUser);
export const updateUser = createApiThunk(userAdministratorRequests.updateUser);
export const deleteUser = createApiThunk(userAdministratorRequests.deleteUser);
export const bulkUserOperations = createApiThunk(
  userAdministratorRequests.bulkUserOperations
);
// INITIAL STATE ===============================================================
const usersEntityAdapter: EntityAdapter<UserAdministrationDto, string> =
  createEntityAdapter<UserAdministrationDto>();

const initialState: UserAdminState = {
  status: 'initial',
  userAdmin: usersEntityAdapter.getInitialState(),
  filters: {
    roles: [],
    teamIds: [],
    clientIds: [],
    hasClient: undefined,
  },
};

// SLICE =======================================================================
export const userAdminSlice = createSlice({
  name: 'userAdmin',
  initialState,
  reducers: {
    userCreated: (
      state: UserAdminState,
      action: PayloadAction<UserAdministrationDto>
    ) => {
      usersEntityAdapter.upsertOne(state.userAdmin, action.payload);
      state.status = 'success';
    },
    userUpdated: (
      state: UserAdminState,
      action: PayloadAction<UserAdministrationDto>
    ) => {
      usersEntityAdapter.upsertOne(state.userAdmin, action.payload);
      state.status = 'success';
    },
    userDeleted: (state: UserAdminState, action: PayloadAction<string>) => {
      usersEntityAdapter.removeOne(state.userAdmin, action.payload);
      state.status = 'success';
    },
    setFilters: (
      state: UserAdminState,
      action: PayloadAction<Partial<UserAdminFiltersType>>
    ) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state: UserAdminState) => {
      state.filters = {
        roles: [],
        teamIds: [],
        clientIds: [],
        hasClient: undefined,
      };
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getUsers.pending, state => {
        state.status = 'loading';
        usersEntityAdapter.removeAll(state.userAdmin);
      })
      .addCase(getUsers.fulfilled, (state, action) => {
        state.status = 'success';
        usersEntityAdapter.upsertMany(state.userAdmin, action.payload);
      })
      .addCase(getUsers.rejected, state => {
        state.status = 'failed';
      })
      .addCase(updateUser.pending, state => {
        state.status = 'loading';
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        usersEntityAdapter.upsertOne(state.userAdmin, action.payload);
        state.status = 'success';
      })
      .addCase(updateUser.rejected, state => {
        state.status = 'failed';
      })
      .addCase(createUser.pending, state => {
        state.status = 'loading';
      })
      .addCase(createUser.fulfilled, (state, action) => {
        usersEntityAdapter.upsertOne(state.userAdmin, action.payload);
        state.status = 'success';
      })
      .addCase(createUser.rejected, state => {
        state.status = 'failed';
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        usersEntityAdapter.removeOne(state.userAdmin, action.payload.id);
        state.status = 'success';
      })
      .addCase(bulkUserOperations.pending, state => {
        state.status = 'loading';
      })
      .addCase(bulkUserOperations.fulfilled, state => {
        // Refresh the users list after bulk operations
        state.status = 'success';
      })
      .addCase(bulkUserOperations.rejected, state => {
        state.status = 'failed';
      });
  },
});

// ACTIONS =====================================================================
export const { setFilters, resetFilters } = userAdminSlice.actions;

// PERSIST CONFIG ==============================================================
const persistConfig: PersistConfig<UserAdminState> = {
  key: 'user_admin',
  storage: localStorage,
  whitelist: ['filters'],
  stateReconciler: autoMergeLevel1,
  version: 1,
};

// REDUCER =====================================================================
export const userAdminReducer = persistReducer(
  persistConfig,
  userAdminSlice.reducer
);

// SELECTORS ===================================================================
const selectUsersSliceState = state =>
  (state as { userAdmin: typeof initialState })?.userAdmin.userAdmin;

const selectFiltersState = state =>
  (state as { userAdmin: typeof initialState })?.userAdmin.filters;

export const {
  selectAll: selectAllUsersAdmin,
  selectById: _selectUserAdminById,
} = usersEntityAdapter.getSelectors(selectUsersSliceState);

export const userAdminSelectors = {
  useSelectAll: () => useAppSelector(selectAllUsersAdmin),
  useSelectAdminUserById: (id: string) => {
    return useAppSelector(state => _selectUserAdminById(state, id));
  },
  useSelectFilters: () => useAppSelector(selectFiltersState),
};
